import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { InventoryItem } from '../../../types/inventory';
import { WarehouseInventoryProductsService } from '../../../api/services/WarehouseInventoryProductsService';
import { MaterialsService } from '../../../api/services/MaterialsService';
import { ColorsService } from '../../../api/services/ColorsService';
import { normalizeTags } from '../../../utils/helpers';
import { formatCurrency } from '../../../utils/formatters/money';
import {
  formatLengthCmFromMm,
  formatWeightGrams,
  parseLocaleDecimal,
} from '../../../utils/localeNumber';
import { getTransferDisplayAtIso } from '../../../utils/inventory-transfers/transferDisplayDate';
import {
  INV_AVAILABILITY_WRITE_OFF_LABEL,
  INV_STATUS_BRANCH_LABEL,
  INV_STATUS_WAREHOUSE_LABEL,
  isAvailabilityWrittenOff,
} from '../../../constants/inventory-status-labels';
import { registrationIsoFromWarehouseListItem } from '../../../utils/inventory-registration-date';
import { isUsableProductImage } from '../../../utils/productImage';

export interface UseInventoryDetailModalProps {
  open: boolean;
  item: InventoryItem | null;
  onEditItem?: (id: string) => void;
  onClose: () => void;
  onSave?: () => void; // Callback after successful save
  onItemUpdate?: (updatedItem: InventoryItem) => void; // Callback to update the item in parent
  /** When true, allows editing warehouse pieces (e.g. Gestión en Almacén with warehouse edit permission) */
  allowEditInWarehouse?: boolean;
  /** When true, allows editing even when item is ASSIGNED (e.g. from Gestión en Sucursal with branch edit permission) */
  allowEditInBranch?: boolean;
  /** When true, hides edit actions (e.g. embedded read-only views in sales) */
  readOnly?: boolean;
  /** When true and canEdit, open already in edit mode (row pencil). */
  initialEditMode?: boolean;
  /** Data-visibility: include cost in edit payload / validation */
  canEditCostUnitPrice?: boolean;
  /** Data-visibility: include sale price in edit payload / validation */
  canEditSalePrice?: boolean;
}

export const useInventoryDetailModal = ({
  open,
  item,
  onEditItem: _onEditItem,
  onClose: _onClose,
  onSave,
  onItemUpdate,
  allowEditInWarehouse = false,
  allowEditInBranch = false,
  readOnly = false,
  initialEditMode = false,
  canEditCostUnitPrice = true,
  canEditSalePrice = true,
}: UseInventoryDetailModalProps) => {
  const formatCmFromMm = (value: number | undefined) => formatLengthCmFromMm(value);
  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  /** Pencil open: apply initialEditMode only once per open cycle (survive Guardar/Cancelar). */
  const initialEditAppliedRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  // Materials state
  const [materials, setMaterials] = useState<any[]>([]);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);

  // Colors state (shared catalog: Propiedades / Catálogo)
  const [colors, setColors] = useState<{ id: number; name: string }[]>([]);
  const [isLoadingColors, setIsLoadingColors] = useState(false);

  // Form state for editable fields (step 2 fields only)
  const [formData, setFormData] = useState({
    costPrice: '',
    salePrice: '',
    weightGrams: '',
    lengthMm: '',
    diameterSize: '',
    colorId: '',
    workshopCode: '',
    materialId: '',
  });

  // Fetch materials
  const fetchMaterials = useCallback(async () => {
    setIsLoadingMaterials(true);
    try {
      const response = await MaterialsService.materialsControllerFindAll(1, 1000);
      if (response && response.data && Array.isArray(response.data)) {
        setMaterials(response.data);
      } else if (Array.isArray(response)) {
        setMaterials(response);
      } else {
        setMaterials([]);
      }
    } catch (error) {
      console.error('Error fetching materials:', error);
      setMaterials([]);
    } finally {
      setIsLoadingMaterials(false);
    }
  }, []);

  const fetchColors = useCallback(async () => {
    setIsLoadingColors(true);
    try {
      const response = await ColorsService.colorsControllerFindAll(1, 1000);
      if (response && response.data && Array.isArray(response.data)) {
        setColors(response.data);
      } else if (Array.isArray(response)) {
        setColors(response);
      } else {
        setColors([]);
      }
    } catch (error) {
      console.error('Error fetching colors:', error);
      setColors([]);
    } finally {
      setIsLoadingColors(false);
    }
  }, []);

  // Fetch materials/colors when edit mode is enabled
  useEffect(() => {
    if (isEditMode && materials.length === 0) {
      fetchMaterials();
    }
  }, [isEditMode, materials.length, fetchMaterials]);

  useEffect(() => {
    if (isEditMode && colors.length === 0) {
      fetchColors();
    }
  }, [isEditMode, colors.length, fetchColors]);

  const { productImageSrc, productImageExpandedUrl, hasExpandableProductImage } = useMemo(() => {
    const p = item?.product;
    if (!p) {
      return {
        productImageSrc: null as string | null,
        productImageExpandedUrl: null as string | null,
        hasExpandableProductImage: false,
      };
    }
    const thumbCandidate =
      (isUsableProductImage(p.thumbnailUrl) ? p.thumbnailUrl : null) ||
      (isUsableProductImage(p.imagePath) ? p.imagePath : null) ||
      null;
    const expandedCandidate =
      (isUsableProductImage(p.imagePath) ? p.imagePath : null) ||
      (isUsableProductImage(p.thumbnailUrl) ? p.thumbnailUrl : null) ||
      null;
    return {
      productImageSrc: thumbCandidate,
      productImageExpandedUrl: expandedCandidate,
      hasExpandableProductImage: isUsableProductImage(expandedCandidate),
    };
  }, [item?.product]);

  useEffect(() => {
    setIsImageExpanded(false);
  }, [item?.id, item?.originalId]);

  const handleProductImageClick = useCallback(() => {
    if (hasExpandableProductImage) {
      setIsImageExpanded(true);
    }
  }, [hasExpandableProductImage]);

  const handleCloseProductImageExpansion = useCallback(() => {
    setIsImageExpanded(false);
  }, []);

  // Initialize form data when item changes or edit mode is enabled
  useEffect(() => {
    if (item && isEditMode) {
      // Resolve catalog IDs from current piece values (name match, same as materials)
      let materialId = '';
      if (item.material && materials.length > 0) {
        const foundMaterial = materials.find((m) => m.name === item.material);
        if (foundMaterial) {
          materialId = foundMaterial.id.toString();
        }
      }

      let colorId = '';
      const itemColorId = (item as any).colorId;
      if (itemColorId != null && itemColorId !== '' && colors.length > 0) {
        const byId = colors.find((c) => c.id === Number(itemColorId));
        if (byId) colorId = byId.id.toString();
      }
      if (!colorId && item.color && colors.length > 0) {
        const foundColor = colors.find(
          (c) => c.name.toLowerCase() === String(item.color).toLowerCase(),
        );
        if (foundColor) colorId = foundColor.id.toString();
      }

      setFormData({
        costPrice: (item as any).costPrice?.toString() || '',
        salePrice: item.price?.toString() || '',
        weightGrams: (item as any).weightGrams?.toString() || '',
        lengthMm:
          (item as any).lengthMm != null ? (Number((item as any).lengthMm) / 10).toString() : '',
        diameterSize: (item as any).diameterSize || '',
        colorId,
        workshopCode: (item as any).workshopCode || '',
        materialId: materialId,
      });
      setError(null);
    }
  }, [item, isEditMode, materials, colors]);

  // Reset UI state when modal closes (X, backdrop, or parent sets open=false)
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setError(null);
      setShowConfirmDialog(false);
      setIsImageExpanded(false);
      initialEditAppliedRef.current = false;
    }
  }, [open]);

  // Check if item can be edited: warehouse (UNASSIGNED + permission) or branch (ASSIGNED + permission)
  const canEdit = useMemo(() => {
    if (readOnly || !item) return false;
    const status = (item as any).originalStatus || item.status;
    const availability = ((item as any).originalAvailabilityStatus ||
      item.availabilityStatus ||
      '') as string;
    if (isAvailabilityWrittenOff(availability)) return false;
    const isUnassigned = status === 'UNASSIGNED' || status === INV_STATUS_WAREHOUSE_LABEL;
    if (isUnassigned) return allowEditInWarehouse;
    return allowEditInBranch;
  }, [item, allowEditInWarehouse, allowEditInBranch, readOnly]);

  // Pencil in list: enter edit once on open; Guardar/Cancelar stay in view until closed
  useEffect(() => {
    if (!open || !initialEditMode || !canEdit || initialEditAppliedRef.current) return;
    initialEditAppliedRef.current = true;
    setIsEditMode(true);
    setError(null);
  }, [open, initialEditMode, canEdit, item?.id]);

  // Determinar color del estado
  const getStatusColor = useCallback((status: string) => {
    return status === INV_STATUS_BRANCH_LABEL ? 'success' : 'info';
  }, []);

  // Formatear precio con moneda
  const formatPrice = useCallback((price: number | undefined) => {
    if (price == null || !Number.isFinite(price)) return formatCurrency(0);
    return formatCurrency(price);
  }, []);

  // Toggle edit mode
  const handleEditClick = useCallback(() => {
    if (!canEdit) {
      setError(`Solo se pueden editar piezas con estado "${INV_STATUS_WAREHOUSE_LABEL}"`);
      return;
    }
    setIsEditMode(true);
  }, [canEdit]);

  // Cancel edit mode
  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    setError(null);
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  // Show confirmation dialog
  const handleSaveClick = useCallback(() => {
    // Validate before showing confirmation (only fields the role can edit)
    if (canEditCostUnitPrice) {
      const costPriceNum = parseLocaleDecimal(formData.costPrice);
      if (costPriceNum == null || costPriceNum < 0) {
        setError('El costo unitario debe ser un número válido mayor o igual a 0');
        return;
      }
    }

    if (canEditSalePrice) {
      // Empty → 0 (unset sale price)
      const salePriceNum =
        formData.salePrice.trim() === '' ? 0 : parseLocaleDecimal(formData.salePrice);
      if (salePriceNum == null || salePriceNum < 0) {
        setError('El precio de venta debe ser un número válido mayor o igual a 0');
        return;
      }
    }

    setShowConfirmDialog(true);
  }, [formData, canEditCostUnitPrice, canEditSalePrice]);

  // Confirm and save changes
  const handleConfirmSave = useCallback(async () => {
    if (!item || !item.originalId) {
      setError('No se puede guardar: ID de pieza no válido');
      setShowConfirmDialog(false);
      return;
    }

    if (!canEdit) {
      setError(`Solo se pueden editar piezas con estado "${INV_STATUS_WAREHOUSE_LABEL}"`);
      setShowConfirmDialog(false);
      return;
    }

    setIsSaving(true);
    setError(null);
    setShowConfirmDialog(false);

    try {
      // Whitelist: only send editable fields. Never send status, locationId, transferredToBranch or availabilityStatus
      // so that editing (warehouse or branch) never changes assignment state or location.
      // Price fields: only include when the role can see/edit them; otherwise keep server values.
      const updatePayload: Record<string, number | string> = {};

      if (canEditCostUnitPrice) {
        updatePayload.costPrice = parseLocaleDecimal(formData.costPrice) ?? 0;
      }
      if (canEditSalePrice) {
        updatePayload.salePrice = parseLocaleDecimal(formData.salePrice) ?? 0;
      }

      if (formData.weightGrams?.trim()) {
        const weightNum = parseLocaleDecimal(formData.weightGrams.trim());
        if (weightNum != null && weightNum > 0) updatePayload.weightGrams = weightNum;
      }
      if (formData.lengthMm?.trim()) {
        const lengthCmNum = parseLocaleDecimal(formData.lengthMm.trim());
        if (lengthCmNum != null && lengthCmNum > 0) updatePayload.lengthMm = lengthCmNum * 10;
      }
      if (formData.diameterSize?.trim()) {
        updatePayload.diameterSize = formData.diameterSize.trim();
      }
      const colorIdStr = formData.colorId?.trim() || '';
      if (colorIdStr) {
        const colorIdNum = parseInt(colorIdStr, 10);
        if (!isNaN(colorIdNum) && colorIdNum > 0) {
          updatePayload.colorId = colorIdNum;
          const selectedColor = colors.find((c) => c.id === colorIdNum);
          if (selectedColor?.name) updatePayload.color = selectedColor.name;
        }
      }
      if (formData.workshopCode?.trim()) {
        updatePayload.workshopCode = formData.workshopCode.trim();
      }
      const materialIdStr = formData.materialId?.trim() || '';
      if (materialIdStr) {
        const materialIdNum = parseInt(materialIdStr, 10);
        if (!isNaN(materialIdNum) && materialIdNum > 0) updatePayload.materialId = materialIdNum;
      }

      await WarehouseInventoryProductsService.warehouseInventoryProductsControllerUpdate(
        item.originalId.toString(),
        updatePayload,
      );

      // Fetch the updated item to refresh the modal
      try {
        const updatedItemResponse =
          await WarehouseInventoryProductsService.warehouseInventoryProductsControllerFindOne(
            item.originalId.toString(),
          );

        // Transform the response to match InventoryItem format (same mapping as branch list for selectableItems/checkbox)
        if (updatedItemResponse) {
          const wasAssigned =
            (item as any).originalStatus === 'ASSIGNED' || item.status === INV_STATUS_BRANCH_LABEL;
          const responseStatus = updatedItemResponse.status ?? item.originalStatus;
          const responseAvailability = (updatedItemResponse as any).availabilityStatus;

          const mapStatusToDisplay = (raw: string | undefined) =>
            raw === 'ASSIGNED'
              ? INV_STATUS_BRANCH_LABEL
              : raw === 'IN_TRANSIT'
                ? 'En Transito'
                : INV_STATUS_WAREHOUSE_LABEL;
          const mapAvailabilityToDisplay = (raw: string | undefined) =>
            raw === 'AVAILABLE'
              ? 'Disponible'
              : raw === 'SOLD'
                ? 'Vendido'
                : raw === 'RESERVED'
                  ? 'Bloqueada'
                  : raw === 'PARTIAL_PAYMENT'
                    ? 'Pago Pendiente'
                    : raw === 'CANCELLED'
                      ? INV_AVAILABILITY_WRITE_OFF_LABEL
                      : (item.availabilityStatus ?? 'Disponible');

          const displayStatus =
            wasAssigned && responseStatus === 'UNASSIGNED'
              ? (item.status ?? INV_STATUS_BRANCH_LABEL)
              : (mapStatusToDisplay(responseStatus ?? undefined) ??
                item.status ??
                INV_STATUS_BRANCH_LABEL);
          const displayAvailability =
            wasAssigned && responseStatus === 'UNASSIGNED'
              ? (item.availabilityStatus ?? 'Disponible')
              : mapAvailabilityToDisplay(responseAvailability);

          const updatedItem: InventoryItem = {
            id: updatedItemResponse.id?.toString() || item.id,
            originalId: updatedItemResponse.id || item.originalId,
            name: updatedItemResponse.product?.name || item.name,
            type: updatedItemResponse.product?.category?.name || item.type,
            createdAt:
              registrationIsoFromWarehouseListItem(updatedItemResponse as any) ||
              updatedItemResponse.createdAt ||
              item.createdAt,
            recordCode:
              updatedItemResponse.inventoryRecordItem?.record?.recordCode || item.recordCode,
            status: displayStatus,
            originalStatus:
              wasAssigned && responseStatus === 'UNASSIGNED'
                ? 'ASSIGNED'
                : responseStatus || item.originalStatus,
            availabilityStatus: displayAvailability,
            updatedAt: updatedItemResponse.updatedAt || item.updatedAt,
            location: updatedItemResponse.location?.name || item.location,
            material: updatedItemResponse.material?.name || item.material,
            price: Number(updatedItemResponse.salePrice) || item.price,
            locationName: updatedItemResponse.location?.name || item.locationName,
            tags: normalizeTags(updatedItemResponse.product?.tags ?? item.tags ?? []),
            costPrice: Number(updatedItemResponse.costPrice) || (item as any).costPrice,
            weightGrams: updatedItemResponse.weightGrams || item.weightGrams,
            lengthMm: updatedItemResponse.lengthMm || item.lengthMm,
            diameterSize: updatedItemResponse.diameterSize || item.diameterSize,
            color: updatedItemResponse.color || item.color,
            workshopCode: updatedItemResponse.workshopCode || item.workshopCode,
            product: updatedItemResponse.product || item.product,
            supplier: updatedItemResponse.supplier
              ? {
                  id: updatedItemResponse.supplier.id,
                  name: updatedItemResponse.supplier.name,
                  status: updatedItemResponse.supplier.status,
                }
              : item.supplier,
            transfer: item.transfer,
          };

          // Update the item in parent component
          if (onItemUpdate) {
            onItemUpdate(updatedItem);
          }
        }
      } catch (fetchError) {
        console.error('Error fetching updated item:', fetchError);
        // Continue even if fetch fails - the onSave callback will refresh the list
      }

      setIsEditMode(false);
      if (onSave) {
        onSave();
      }
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError(
        err?.body?.message ||
          err?.response?.data?.message ||
          'No se pudo actualizar la pieza. Intente de nuevo.',
      );
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    item,
    formData,
    canEdit,
    canEditCostUnitPrice,
    canEditSalePrice,
    onSave,
    materials,
    colors,
    onItemUpdate,
  ]);

  // Utilidad y margen potenciales (precio de venta − costo; pieza aún no vendida)
  const marginInfo = useMemo(() => {
    if (!item) return null;

    const costPrice = (item as any).costPrice;
    const salePrice = item.price;

    if (salePrice && salePrice > 0 && costPrice != null) {
      const margin = salePrice - costPrice;
      const marginPercentage = (margin / salePrice) * 100;

      return {
        margin,
        marginPercentage,
        isPositiveMargin: margin >= 0,
      };
    }

    return null;
  }, [item]);

  // Obtener información de tags
  const tagsInfo = useMemo(() => {
    if (!item?.tags) return { hasTags: false, tags: [] };

    // Normalizar tags: pueden venir como strings o como objetos Tag
    const normalizedTags = item.tags.map((tag: any) => {
      if (typeof tag === 'string') {
        return { name: tag, color: '#3b82f6' }; // Color por defecto si es string
      }
      // Asegurarse de que el objeto tenga name y color
      return {
        name: tag?.name || tag?.label || '',
        color: tag?.color || '#3b82f6',
        id: tag?.id,
      };
    });

    // Debug: ver qué tags tenemos

    return {
      hasTags: normalizedTags.length > 0,
      tags: normalizedTags,
    };
  }, [item?.tags]);

  // Obtener información de propiedades
  const propertiesInfo = useMemo(() => {
    if (!item) return {};

    return {
      weight: formatWeightGrams(item.weightGrams),
      length: formatCmFromMm(item.lengthMm),
      diameter: item.diameterSize || 'No especificado',
      color: item.color || 'No especificado',
      workshopCode: item.workshopCode || 'No especificado',
      material: item.material || 'No especificado',
    };
  }, [item]);

  // Obtener información de ubicación
  const locationInfo = useMemo(() => {
    if (!item) return {};

    return {
      locationName: item.locationName || 'No especificada',
    };
  }, [item]);

  // Obtener información de fechas (transferencia: misma fuente que las tablas de sucursal)
  const datesInfo = useMemo(() => {
    if (!item) return {};

    const transferAt =
      getTransferDisplayAtIso(item.transfer, item.firstAssignedAt, item.transferredAt) ||
      item.transferredAt ||
      item.firstAssignedAt ||
      item.updatedAt;

    return {
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      transferAt,
    };
  }, [item]);

  return {
    // Utilidades
    getStatusColor,
    formatPrice,

    // Edit mode
    isEditMode,
    canEdit,
    isSaving,
    error,
    formData,
    handleEditClick,
    handleCancelEdit,
    handleSaveClick,
    handleConfirmSave,
    handleFieldChange,
    showConfirmDialog,
    setShowConfirmDialog,

    // Materials / Colors (catalog)
    materials,
    isLoadingMaterials,
    colors,
    isLoadingColors,

    // Información calculada
    marginInfo,
    tagsInfo,
    propertiesInfo,
    locationInfo,
    datesInfo,

    // Imagen (visor a pantalla completa, mismo patrón que ProductDetailModal)
    productImageSrc,
    productImageExpandedUrl,
    hasExpandableProductImage,
    isImageExpanded,
    handleProductImageClick,
    handleCloseProductImageExpansion,
  };
};
